import { Asset, AssetManager, assetManager, Director, director, Scene } from 'cc';
import Game from './Game';
import IPreloader from './IPreloader';
import AssetLoadingConfigType, {
    AssetName,
    assetNameToAssetTypeMap,
    assetTypeToAssetNameMap,
    LoadingType,
    PathLoadingConfigType,
} from './Config/AssetLoadingConfigType';
import AssetCache from './Toolkit/Helper/Loading/AssetCache';
import LoadingTask from './Toolkit/Helper/Loading/LoadingTask/LoadingTask';
import CompositeLoadingTask from './Toolkit/Helper/Loading/LoadingTask/CompositeLoadingTask';
import Delay from './Toolkit/Utils/Delay';
import { joinURL } from './Toolkit/Utils/Utility';

export namespace TaskNamingHelper {
    type LoadTaskNameProps = { loadingType?: LoadingType; providerName?: string; additionalName?: string; configName?: string; assetName?: string };

    const loadingTypeToNameMap: Record<LoadingType, string> = {
        [LoadingType.PRELOAD]: 'PRELOAD',
        [LoadingType.LAZY]: 'LAZY',
        [LoadingType.ON_DEMAND]: 'ON_DEMAND',
    };

    export function generateProviderName(configs: AssetLoadingConfig[]): string {
        return configs.reduce((acc, config) => `${acc}${acc ? '|' : ''}${config.name}`, '');
    }

    export function generateConfigName({ bundleName, prefix = '', uniqueID = '' }: AssetLoadingConfigType): string {
        return `${bundleName}${prefix && `/${prefix}`}${uniqueID && `#${uniqueID}`}`;
    }

    export function concatTaskName({ loadingType, providerName, configName, assetName }: LoadTaskNameProps): string;
    export function concatTaskName(name: string, { loadingType, providerName, configName, assetName }: LoadTaskNameProps): string;
    export function concatTaskName(arg1: string | LoadTaskNameProps, arg2?: LoadTaskNameProps): string {
        let name = typeof arg1 === 'string' ? arg1 : '';
        const { loadingType, providerName, configName, assetName } = typeof arg1 === 'string' ? arg2 : arg1;
        if (loadingType !== undefined) {
            name = `${loadingTypeToNameMap[loadingType]} :: ${name}`;
        }
        if (providerName) {
            name += `${providerName}`;
        }
        if (configName) {
            name += ` @ ${configName}`;
        }
        if (assetName) {
            name += ` ~ ${assetName}`;
        }
        return name;
    }
}

type FullPathPathConfig = Required<Exclude<Required<PathLoadingConfigType>, string>>;

class AssetLoadingConfig {
    private static readonly DEFAULT_PATH_CONFIG: FullPathPathConfig = {
        type: LoadingType.PRELOAD,
        path: '',
        isDir: false,
        assetPrefix: false,
        shouldLoad: true,
    };
    public readonly name: string;
    public readonly bundleName: string;
    public readonly prefix: string;
    public readonly path: Partial<Record<AssetName, Record<string, FullPathPathConfig>>>;
    private _bundle: AssetManager.Bundle = null;

    constructor(
        config: AssetLoadingConfigType,
        private readonly _cache: AssetCache,
        private readonly _getProviderName: () => string,
    ) {
        this.name = TaskNamingHelper.generateConfigName(config);
        this.bundleName = config.bundleName;
        this.prefix = config.prefix || '';
        this.path = Object.entries(config.path).reduce(
            (pathObj, [assetName, pathConfig]) => {
                pathObj[assetName as AssetName] = Object.entries(pathConfig).reduce(
                    (pathConfigMap, [key, path]) => {
                        if (!path) {
                            delete pathConfigMap[key];
                        } else {
                            pathConfigMap[key] = this._createFullPathConfig(path);
                        }
                        return pathConfigMap;
                    },
                    {} as Record<string, FullPathPathConfig>,
                );
                return pathObj;
            },
            {} as Partial<Record<AssetName, Record<string, FullPathPathConfig>>>,
        );
    }

    public preload(): LoadingTask {
        return this._loadWithType(LoadingType.PRELOAD);
    }

    public async load<T extends typeof Asset>(assetType: T, assetKey: string) {
        const pathConfig = this._findPathConfig(assetType, assetKey);
        if (!pathConfig) {
            return null;
        }
        // ensure bundle is loaded before loading asset
        await this._loadBundle();
        if (pathConfig.isDir) {
            return this._loadDir(assetType, pathConfig.path, pathConfig.assetPrefix);
        }
        return this._loadAsset(assetType, assetKey, pathConfig.path);
    }

    public lazyLoad() {
        return this._loadWithType(LoadingType.LAZY);
    }

    public onDemandLoad() {
        return this._loadWithType(LoadingType.ON_DEMAND);
    }

    /**
     * 對這個 config 中每一個已載入的 ON_DEMAND asset 呼叫 decRef 並清除 cache
     * @returns 被釋放的 asset key 清單
     */
    public releaseOnDemand(): string[] {
        const releasedKeys: string[] = [];
        Object.entries(this.path).forEach(([assetName, assetPathMap]) => {
            const assetType = assetNameToAssetTypeMap[assetName as AssetName];
            Object.entries(assetPathMap)
                .filter(([, pathConfig]) => pathConfig.type === LoadingType.ON_DEMAND)
                .forEach(([assetKey]) => {
                    const asset = this._cache.get(assetType, assetKey);
                    if (!asset) {
                        return;
                    }
                    asset.decRef();
                    this._cache.clear(assetType, assetKey);
                    releasedKeys.push(assetKey);
                });
        });
        return releasedKeys;
    }

    private _loadWithType(type: LoadingType.PRELOAD | LoadingType.LAZY | LoadingType.ON_DEMAND) {
        const taskName = TaskNamingHelper.concatTaskName(this._getProviderName(), { loadingType: type, configName: this.name });
        const task = new CompositeLoadingTask(taskName);

        // load bundle
        const loadBundlePromise = this._loadBundle();
        if (!this._bundle) {
            task.addSimpleTask(TaskNamingHelper.concatTaskName(task.name, { assetName: 'bundle' }), loadBundlePromise);
        }

        // load assets
        Object.entries(this.path).forEach(async ([assetName, assetPathMap]) => {
            const assetType = assetNameToAssetTypeMap[assetName as AssetName];
            Object.entries(assetPathMap)
                .filter(([, pathConfig]) => pathConfig.type === type)
                .forEach(([assetKey, pathConfig]) => {
                    const loadAssetTaskName = TaskNamingHelper.concatTaskName(task.name, { assetName: assetKey });
                    const { path, isDir, assetPrefix, shouldLoad } = pathConfig;
                    if (!shouldLoad) {
                        return;
                    }
                    if (isDir) {
                        const loadDirTask = task.addTask(new LoadingTask(loadAssetTaskName));
                        loadBundlePromise.then(() => {
                            this._loadDir(assetType, path, assetPrefix, loadDirTask.progress.bind(loadDirTask), loadDirTask.finish.bind(loadDirTask));
                        });
                    } else {
                        task.addSimpleTask(
                            loadAssetTaskName,
                            loadBundlePromise.then(() => this._loadAsset(assetType, assetKey, path)),
                        );
                    }
                });
        });

        if (!task.subTaskCount) {
            return LoadingTask.Success;
        }

        return task;
    }

    private async _loadBundle(): Promise<AssetManager.Bundle> {
        if (this._bundle) {
            return this._bundle;
        }
        return new Promise<AssetManager.Bundle>((resolve, reject) => {
            assetManager.loadBundle(this.bundleName, (err, bundle) => {
                if (err) {
                    reject(err);
                    return;
                }
                this._bundle = bundle;
                resolve(bundle);
            });
        });
    }

    private async _loadAsset<T extends typeof Asset>(assetType: T, assetKey: string, path: string) {
        let asset = this._cache.get(assetType, assetKey);
        if (!asset) {
            asset = await this._bundle.loadAsync(path, assetType, this.prefix);
            this._cache.set(assetType, assetKey, asset);
        }
        return asset;
    }

    private async _loadDir<T extends typeof Asset>(
        assetType: T,
        path: string,
        assetPrefix?: string | boolean,
        onProgress?: (progress: number) => void,
        onCompleted?: (error: Error, assets: InstanceType<T>[]) => void,
    ) {
        let actualAssetPrefix: string = '';
        if (assetPrefix) {
            actualAssetPrefix = typeof assetPrefix === 'string' ? assetPrefix : path.split('/').pop() ?? '';
        }
        return this._bundle.loadDirAsync(path, assetType, this.prefix, onProgress, (error: Error, assets: InstanceType<T>[]) => {
            if (assets?.length) {
                // must cache all assets before calling onCompleted
                if (actualAssetPrefix) {
                    assets.forEach((asset) => {
                        this._cache.set(assetType, `${actualAssetPrefix}_${asset.name}`, asset);
                    });
                } else {
                    assets.forEach((asset) => {
                        this._cache.set(assetType, asset.name, asset);
                    });
                }
            }
            onCompleted?.(error, assets);
        });
    }

    /**
     * find path config of target asset
     * @param assetType target asset type
     * @param assetKey target asset key
     * @returns full path path config if asset exist, otherwise return null
     */
    private _findPathConfig<T extends typeof Asset>(assetType: T, assetKey: string): FullPathPathConfig {
        const assetPathMap = this.path[assetTypeToAssetNameMap.get(assetType)];
        if (!assetPathMap) {
            return null;
        }

        // check if asset exist in path config
        if (assetPathMap[assetKey]) {
            return assetPathMap[assetKey];
        }

        // check if directory of asset exist in path config
        const [directoryKey, assetName] = assetKey.split('/');
        if (assetName) {
            const directoryPathConfig = assetPathMap[directoryKey];
            if (directoryPathConfig) {
                // generate full path path config of target asset
                const targetAssetPathConfig: FullPathPathConfig = {
                    path: joinURL(directoryPathConfig.path, assetName),
                    type: LoadingType.ON_DEMAND,
                    isDir: false,
                    assetPrefix: false,
                    shouldLoad: true,
                };
                assetPathMap[assetKey] = targetAssetPathConfig;
                return targetAssetPathConfig;
            }
        }

        // cannot find asset
        return null;
    }

    private _createFullPathConfig(pathConfig: PathLoadingConfigType): FullPathPathConfig {
        const tempPathConfig = typeof pathConfig === 'string' ? { type: LoadingType.PRELOAD, path: pathConfig } : pathConfig;
        return { ...AssetLoadingConfig.DEFAULT_PATH_CONFIG, ...tempPathConfig };
    }
}

export enum LazyLoadingState {
    NONE,
    NOT_STARTED,
    IN_PROGRESS,
    COMPLETED,
    FAILED,
}

export default class BundleAssetProvider implements IPreloader {
    public static readonly EVENT_LAZY_LOADING_STATE_CHANGED = 'LAZY_LOADING_STATE_CHANGED';
    private static readonly LAZY_LOADING_STATE_MAP: Record<string, LazyLoadingState> = new Proxy(
        {},
        {
            set: (target, prop: string, value: LazyLoadingState) => {
                if (target[prop] !== undefined) {
                    if (value < target[prop]) {
                        throw new Error(
                            `invalid lazy loading state change for config: ${prop} from ${LazyLoadingState[target[prop]]} to ${LazyLoadingState[value]}`,
                        );
                    }
                    if (target[prop] !== value) {
                        Game.event.emit(BundleAssetProvider.EVENT_LAZY_LOADING_STATE_CHANGED, prop, value);
                    }
                }
                return Reflect.set(target, prop, value);
            },
        },
    );
    public readonly name: string = '';
    private readonly _cache: AssetCache = new AssetCache();
    private readonly _assetLoadingConfigs: AssetLoadingConfig[] = [];
    private readonly _preloadStartPromise: Promise<void> = null;
    private _preloadTask: CompositeLoadingTask = null;
    private _preloadStartResolve: () => void = null;

    constructor(assetLoadingConfig: AssetLoadingConfigType | AssetLoadingConfigType[]) {
        this._assetLoadingConfigs = (Array.isArray(assetLoadingConfig) ? assetLoadingConfig : [assetLoadingConfig]).map((config) => {
            const configInstance = new AssetLoadingConfig(config, this._cache, () => this.name);
            if (config.triggerLazyLoadingSceneTags?.length) {
                const callback = async (scene: Scene) => {
                    if (config.triggerLazyLoadingSceneTags?.some((tag) => scene.name.includes(tag))) {
                        if (config.deferredLazyLoading) {
                            await config.deferredLazyLoading(scene);
                        }

                        // to reduce cpu overhead of lazy loading to the max extent, here we set maxConcurrency and maxRequestsPerFrame to 1
                        if (config.lazyLoadingMaxConcurrency) {
                            assetManager.presets.bundle.maxConcurrency = config.lazyLoadingMaxConcurrency;
                        }
                        if (config.lazyLoadingMaxRequestsPerFrame) {
                            assetManager.presets.bundle.maxRequestsPerFrame = config.lazyLoadingMaxRequestsPerFrame;
                        }
                        const task = configInstance.lazyLoad();
                        task.waitForFinish().then(() => {
                            BundleAssetProvider.LAZY_LOADING_STATE_MAP[configInstance.name] = task.isSuccess
                                ? LazyLoadingState.COMPLETED
                                : LazyLoadingState.FAILED;
                        });
                        BundleAssetProvider.LAZY_LOADING_STATE_MAP[configInstance.name] = LazyLoadingState.IN_PROGRESS;
                        director.off(Director.EVENT_AFTER_SCENE_LAUNCH, callback);
                    }
                };
                director.on(Director.EVENT_AFTER_SCENE_LAUNCH, callback);
                BundleAssetProvider.LAZY_LOADING_STATE_MAP[configInstance.name] = LazyLoadingState.NOT_STARTED;
            } else {
                BundleAssetProvider.LAZY_LOADING_STATE_MAP[configInstance.name] = LazyLoadingState.NONE;
            }

            return configInstance;
        });
        if (!this._assetLoadingConfigs?.length) {
            throw new Error('BundleAssetProvider must have at least one config');
        }
        this.name = TaskNamingHelper.generateProviderName(this._assetLoadingConfigs);
        this._preloadStartPromise = new Promise((resolve) => {
            this._preloadStartResolve = resolve;
        });
    }

    public get assetLoadingConfigs() {
        return this._assetLoadingConfigs;
    }

    public static getLazyLoadingState(configName: string): LazyLoadingState {
        if (!(configName in BundleAssetProvider.LAZY_LOADING_STATE_MAP)) {
            throw new Error(`Cannot find lazy loading state of config: ${configName}`);
        }
        return BundleAssetProvider.LAZY_LOADING_STATE_MAP[configName];
    }

    public preload(): LoadingTask {
        // return existing task if already started
        if (this._preloadTask) {
            return this._preloadTask;
        }

        // start preloading
        const preloadTaskName = TaskNamingHelper.concatTaskName({
            loadingType: LoadingType.PRELOAD,
            providerName: this.name,
        });
        this._preloadTask = new CompositeLoadingTask(preloadTaskName);
        this._assetLoadingConfigs.forEach((assetLoadingConfig) => this._preloadTask.addTask(assetLoadingConfig.preload()));

        // notify preload start
        this._preloadStartResolve();
        return this._preloadTask;
    }

    public loadOnDemand(): LoadingTask {
        const taskName = TaskNamingHelper.concatTaskName({
            loadingType: LoadingType.ON_DEMAND,
            providerName: this.name,
        });
        const task = new CompositeLoadingTask(taskName);
        this._assetLoadingConfigs.forEach((assetLoadingConfig) => task.addTask(assetLoadingConfig.onDemandLoad()));
        return task;
    }

    public releaseOnDemand(): string[] {
        return this._assetLoadingConfigs.reduce(
            (releasedKeys, assetLoadingConfig) => releasedKeys.concat(assetLoadingConfig.releaseOnDemand()),
            [] as string[],
        );
    }

    public set<T extends typeof Asset>(assetName: string, assetType: T, asset: InstanceType<T>) {
        this._cache.set(assetType, assetName, asset);
    }

    public clear<T extends typeof Asset>(assetName: string, assetType: T) {
        this._cache.clear(assetType, assetName);
    }

    public get<T extends typeof Asset>(assetName: string, assetType: T): InstanceType<T> {
        return this._cache.get(assetType, assetName);
    }

    public async load<T extends typeof Asset>(assetKey: string, assetType: T, multi?: false): Promise<InstanceType<T>>;
    public async load<T extends typeof Asset>(assetKey: string, assetType: T, multi: true): Promise<InstanceType<T>[]>;
    public async load<T extends typeof Asset>(assetKey: string, assetType: T, multi: boolean = false): Promise<InstanceType<T> | InstanceType<T>[]> {
        const assets = (await Promise.all(this._assetLoadingConfigs.map((assetLoadingConfig) => assetLoadingConfig.load(assetType, assetKey)))).find(Boolean);
        if (!assets) {
            throw new Error(`Cannot find asset: ${assetKey} of type: ${assetType.name} in provider: ${this.name}`);
        }
        if (multi) {
            return assets instanceof Array ? assets : [assets];
        }
        return assets instanceof Array ? assets[0] : assets;
    }

    public getAll<T extends typeof Asset>(assetType: T): Record<string, InstanceType<T>> {
        return this._cache.getAll(assetType);
    }

    public async waitForLoaded(): Promise<void> {
        if (!this._preloadTask) {
            await this._preloadStartPromise;
        }
        await this._preloadTask.waitForFinish();
        if (!this._preloadTask.isSuccess) {
            await Delay.forever();
        }
    }
}
